/* ================================================================
   BRITTEN CUSTOM: Real Brides session id and cart source (rb_sid, rb_source)
   ================================================================
   Added:       September 2026
   Author:      Claude / Matthew
   Location:    assets/britten-rb-session.js
   Loaded by:   snippets/britten-dress-match.liquid (the Real Brides archive and
                the older gallery page), snippets/britten-dress-finder.liquid and
                snippets/britten-pdp-metrics.liquid (product pages). Each loads it
                deferred, BEFORE its own scripts. Included twice on one page it does
                nothing the second time.

   PURPOSE:
   Ties a Real Brides visit to the order it led to. Two small things:
   (1) rb_sid, one random id per browser, added to the analytics events the
       dress finder, the archive and the product-page strip send, so a purchase
       days later can be joined back to what she looked at.
   (2) rb_source, a coarse label of where she started ('dress_finder' |
       'archive' | 'pdp_strip'), written to the cart as a cart attribute with
       rb_sid, so both reach the order (the custom pixel reads them at checkout).

   API (window.BrittenRb):
     sid()          -> string. The id, or '' when analytics consent is not
                       allowed or not known. Creates it on first use.
     withSid(obj)   -> obj, with obj.rb_sid set when sid() has one.
     touch(source)  -> void. Fire and forget: writes rb_source (first touch
                       wins) and rb_sid to the cart. Never throws, never waits.

   HOW rb_sid WORKS:
   1. Consent. Shopify's Customer Privacy API decides: only when
      Shopify.customerPrivacy.analyticsProcessingAllowed() is true is the id
      created, stored or returned. False or not yet known (the API has not
      loaded, or it is not there) gives no id at all. An id stored earlier is
      deleted when the API says analytics is not allowed.
   2. Storage. localStorage 'britten.rb.sid.v1' = { id, exp }, about 90 days
      from creation, then a new id. Every storage call is inside try/catch;
      if storage is blocked the id lasts for that page only.
   3. The id is 24 random hex characters (crypto.getRandomValues). It holds
      nothing about her: no name, email, photo or dress.

   HOW touch() WORKS:
   1. rb_source is a label with three possible values, not an identifier, and
      goes on the cart whether or not analytics is allowed. rb_sid goes with it
      only when sid() has one.
   2. First touch wins. localStorage 'britten.rb.touch.v1' = { s, sid, c, t }
      says a source has already been written for this cart (c = the `cart`
      cookie value, so a new cart after a purchase is a new first touch; it
      also lapses after 14 days, the life of a cart). A later touch never
      overwrites rb_source. If rb_sid was not available the first time (consent
      came later) it is added on a later touch, on its own.
   3. The write is POST /cart/update.js with { attributes: { ... } }, fetch with
      keepalive so it survives the click's navigation. The URL is passed as a
      URL object, not a string, on purpose: britten-length-surcharge-cart and
      britten-veil-cart-limit wrap fetch and re-check the cart after any string
      URL containing /cart/update. This write changes no line item, so they
      have nothing to do. Attribute keys are rb_source and rb_sid; the wedding
      date writer (britten-cart-wedding-date) uses 'Wedding date' and
      'Wedding date (ISO)'. /cart/update.js merges attributes by key, and
      nothing here reads the cart first, so the two cannot overwrite each other.

   NEVER in an id, event or attribute: the uploaded image, email, names or free
   text. Public slugs and product handles are fine.

   THEME DEPENDENCY:
   Shopify.customerPrivacy (Shopify's Customer Privacy API), the `cart` cookie,
   /cart/update.js. Read by assets/britten-dress-match-core.js (core.emit),
   assets/britten-archive.js (BA.track), assets/britten-dress-finder.js,
   snippets/britten-pdp-metrics.liquid and snippets/britten-real-brides-pdp.liquid.

   CHANGELOG:
   - 29 Sep 2026: initial build.
   ================================================================ */
(function () {
  'use strict';

  // === CONFIGURATION ===
  var FEATURE_ENABLED = true;
  var DEBUG_MODE = false;

  var SID_KEY = 'britten.rb.sid.v1';
  var TOUCH_KEY = 'britten.rb.touch.v1';
  var SID_TTL_MS = 90 * 24 * 60 * 60 * 1000;
  var TOUCH_TTL_MS = 14 * 24 * 60 * 60 * 1000;
  var SOURCES = { dress_finder: true, archive: true, pdp_strip: true };

  function log(msg) {
    if (DEBUG_MODE && window.console) console.log('[Britten RB] ' + msg);
  }

  if (!FEATURE_ENABLED) return;
  if (window.BrittenRb) return;

  // ---- storage (always try/catch: private mode, blocked, full) ----
  function lsGet(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } }
  function lsSet(key, value) { try { window.localStorage.setItem(key, value); } catch (e) { /* noop */ } }
  function lsDel(key) { try { window.localStorage.removeItem(key); } catch (e) { /* noop */ } }
  function lsJson(key) {
    var raw = lsGet(key);
    if (!raw) return null;
    try { var v = JSON.parse(raw); return v && typeof v === 'object' ? v : null; } catch (e) { return null; }
  }

  // ---- consent: Shopify's Customer Privacy API ----
  var featuresRequested = false;
  function privacyApi() {
    try {
      var s = window.Shopify;
      var cp = s && s.customerPrivacy;
      return cp && typeof cp.analyticsProcessingAllowed === 'function' ? cp : null;
    } catch (e) { return null; }
  }
  // Ask Shopify to load the API if this page has not (its documented route), once.
  function requestPrivacyApi() {
    if (featuresRequested) return;
    featuresRequested = true;
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { /* noop */ });
      }
    } catch (e) { /* noop */ }
  }
  // 'yes' | 'no' | 'unknown'. Unknown is treated as no.
  function analyticsConsent() {
    var cp = privacyApi();
    if (!cp) { requestPrivacyApi(); return 'unknown'; }
    try { return cp.analyticsProcessingAllowed() === true ? 'yes' : 'no'; } catch (e) { return 'unknown'; }
  }

  // ---- rb_sid ----
  var pageSid = ''; // used only when storage is blocked, so one page still agrees with itself
  function randomId() {
    var out = '';
    try {
      var c = window.crypto || window.msCrypto;
      if (c && c.getRandomValues) {
        var bytes = new Uint8Array(12);
        c.getRandomValues(bytes);
        for (var i = 0; i < bytes.length; i++) out += ('0' + bytes[i].toString(16)).slice(-2);
        return out;
      }
    } catch (e) { /* fall through */ }
    while (out.length < 24) out += Math.floor(Math.random() * 16).toString(16);
    return out;
  }
  function validId(id) { return typeof id === 'string' && /^[0-9a-f]{24}$/.test(id); }

  function sid() {
    try {
      var consent = analyticsConsent();
      if (consent === 'no') { lsDel(SID_KEY); pageSid = ''; return ''; }
      if (consent !== 'yes') return '';
      var now = Date.now();
      var stored = lsJson(SID_KEY);
      if (stored && validId(stored.id) && typeof stored.exp === 'number' && stored.exp > now) return stored.id;
      if (pageSid) return pageSid;
      var id = randomId();
      pageSid = id;
      lsSet(SID_KEY, JSON.stringify({ id: id, exp: now + SID_TTL_MS }));
      log('rb_sid created');
      return id;
    } catch (e) { return ''; }
  }

  function withSid(obj) {
    try {
      var id = sid();
      if (id && obj && typeof obj === 'object') obj.rb_sid = id;
    } catch (e) { /* noop */ }
    return obj;
  }

  // ---- cart attributes ----
  function cartToken() {
    try {
      var m = /(?:^|;\s*)cart=([^;]*)/.exec(document.cookie || '');
      return m ? m[1] : '';
    } catch (e) { return ''; }
  }

  function postAttributes(attrs) {
    try {
      // A URL object, not a string: see HOW touch() WORKS, item 3.
      var url = new URL('/cart/update.js', window.location.origin);
      var p = window.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify({ attributes: attrs })
      });
      if (p && typeof p.catch === 'function') p.catch(function () { /* fire and forget */ });
    } catch (e) { /* never throw */ }
  }

  function touch(source) {
    try {
      if (!SOURCES[source]) return;
      var now = Date.now();
      var token = cartToken();

      // Has a source already been written for THIS cart?
      var marker = lsJson(TOUCH_KEY);
      if (marker && (typeof marker.t !== 'number' || now - marker.t > TOUCH_TTL_MS)) marker = null;
      if (marker && marker.c && token && marker.c !== token) marker = null; // a new cart: a new first touch
      if (marker && !marker.c && token) marker.c = token; // the cart our first write created

      var id = sid();
      var attrs = {};
      if (!marker) attrs.rb_source = source;
      if (id && !(marker && marker.sid)) attrs.rb_sid = id;

      if (!attrs.rb_source && !attrs.rb_sid) {
        if (marker) lsSet(TOUCH_KEY, JSON.stringify(marker));
        return;
      }
      lsSet(TOUCH_KEY, JSON.stringify({
        s: marker ? marker.s : source,
        sid: !!(attrs.rb_sid || (marker && marker.sid)),
        c: marker ? marker.c : token,
        t: marker ? marker.t : now
      }));
      postAttributes(attrs);
      log('cart attributes: ' + Object.keys(attrs).join(', '));
    } catch (e) { /* never throw */ }
  }

  window.BrittenRb = { sid: sid, withSid: withSid, touch: touch };
})();
